import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useOutletContext } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

import { createApiClient, resolveApiBase } from '../api.js';
import { ContextActions } from '../components/ContextActions.jsx';
import {
  XIcon,
  CheckIcon,
  RefreshIcon,
  SendIcon,
  UserIcon,
  ArrowRightIcon,
  AddIcon,
} from '../components/icons';

/**
 * Help inbox — operator-facing surface for RFC-026.
 *
 * Master-detail layout: filterable list on the left, full thread on
 * the right.  Status (Open / Awaiting / Resolved) is a filter pill
 * rather than a column — the column layout was squashing cards on
 * any laptop-width display and hiding most of the metadata operators
 * actually triage on.  When a thread is open we drop the polling
 * cadence to 3 s so the conversation feels realtime, including a
 * typing indicator and an in-place reply that doesn't lose focus on
 * each render.
 *
 * Operator actions: assign / unassign, mark resolved / re-open,
 * canned acknowledgement, promote a great reply into the FAQ
 * library.
 *
 * The thread view also renders a **Context map** — every key the
 * SDK widget captured at submit time (analytics session id, host
 * tenant id, viewport, page route, picked elements, …) grouped by
 * source.  Operators read the full picture without leaving the inbox.
 */

const STATUS_FILTERS = [
  { id: 'all',           label: 'All' },
  { id: 'open',          label: 'Open',          hint: 'Waiting for you to reply.' },
  { id: 'awaiting_user', label: 'Awaiting user', hint: 'You replied — waiting on the user.' },
  { id: 'resolved',      label: 'Resolved',      hint: 'Closed; user can re-open by replying.' },
];

const POLL_MS = 30_000;
const FAST_POLL_MS = 3_000;
const TYPING_PING_MS = 2_500;

const CANNED_RESPONSES = [
  { id: 'ack',       label: 'Acknowledge',     body: "Thanks for getting in touch — we've got your message and will reply shortly." },
  { id: 'looking',   label: 'Looking into it', body: "Looking into this for you now — back in a few minutes." },
  { id: 'need-info', label: 'Need more info',  body: "Could you share a screenshot or the exact error you're seeing? Helps us narrow it down." },
];

/** Project-scoped variant — locks the project filter so the inbox
 *  only ever shows threads from this project.  Same component, just
 *  pre-pinned.  Used as the per-project Help inbox tab.
 */
export function ProjectHelpInbox() {
  const { project } = useOutletContext() || {};
  return <HelpInbox lockedProjectId={project?.project_id || null} />;
}

export function HelpInbox({ lockedProjectId = null } = {}) {
  const { getToken } = useAuth();
  const { user } = useUser();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  const meId = user?.id || '';
  const meName = user?.fullName || user?.firstName || user?.primaryEmailAddress?.emailAddress || 'You';
  const meEmail = user?.primaryEmailAddress?.emailAddress || '';

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(null);
  const [promoteTitle, setPromoteTitle] = useState('');
  const [promoteRoutes, setPromoteRoutes] = useState('');
  // Editable article body — pre-filled from the reply but the
  // operator should polish it before it ships to end users (the raw
  // reply often references the user by name or includes a one-off
  // pleasantry that doesn't read well as a public answer).
  const [promoteBody, setPromoteBody] = useState('');
  // Single-project filter for the inbox view.  `null` = all projects
  // the operator has access to (still server-side scoped to their
  // ``admin_user.project_ids``).  Persisted to localStorage so the
  // filter survives a page refresh.
  // When ``lockedProjectId`` is set (mounted from
  // /projects/:slug/help/inbox), the filter is fixed to that project
  // and the user-facing picker is hidden — this view is the
  // tenant-wide inbox scoped to one project, full stop.  Otherwise
  // the user can pick / clear the filter as before.
  const [projectFilter, setProjectFilter] = useState(() => {
    if (lockedProjectId) return lockedProjectId;
    try { return localStorage.getItem('spot.help.projectFilter') || null; } catch { return null; }
  });
  useEffect(() => {
    if (lockedProjectId) setProjectFilter(lockedProjectId);
  }, [lockedProjectId]);
  const [projects, setProjects] = useState([]);
  // Inbox-scale controls: free-text search across body / user /
  // route / tags, plus assignee and status filter pills.  The
  // status filter merged into the same row as assignee — the
  // three-column status layout was squashing cards (feedback #4).
  const [search, setSearch] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // all | mine | unassigned
  const [statusFilter, setStatusFilter] = useState('all');
  const lastTypingPing = useRef(0);

  // Load the project list once — used to label the filter pills.  The
  // inbox renders projects the operator can actually see (server
  // filters by their `project_ids` on every list call).
  useEffect(() => {
    let alive = true;
    api('/v1/admin/projects')
      .then((data) => { if (alive) setProjects(data?.items || []); })
      .catch(() => { /* non-fatal — pills just show ids */ });
    return () => { alive = false; };
  }, [api]);

  // 30-day stats roll-up — MTTR, MTTFR, volume, per-operator
  // leaderboard.  Refetched whenever the project filter changes so
  // the headline numbers match the inbox below.
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    const qs = projectFilter ? `?days=30&project_id=${encodeURIComponent(projectFilter)}` : '?days=30';
    api(`/v1/admin/help/stats${qs}`)
      .then((data) => { if (alive) setStats(data || null); })
      .catch(() => { /* stats are non-essential — inbox still works */ });
    return () => { alive = false; };
  }, [api, projectFilter, items.length]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const qs = projectFilter ? `?project_id=${encodeURIComponent(projectFilter)}` : '';
      const resp = await api(`/v1/admin/help/requests${qs}`);
      setItems(resp?.items || []);
      setUnread(resp?.unread_count || 0);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [api, projectFilter]);

  const setActiveProject = useCallback((pid) => {
    setProjectFilter(pid);
    try {
      if (pid) localStorage.setItem('spot.help.projectFilter', pid);
      else localStorage.removeItem('spot.help.projectFilter');
    } catch { /* ignore quota errors */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Deep-link to a specific thread via the URL hash — used by the
  // Feedback view's "Open thread →" links so clicking actually opens
  // the conversation rather than just scrolling the inbox.  Re-applies
  // when the hash changes (in-app navigation via React Router doesn't
  // fire the DOM `hashchange` event, so we listen to useLocation
  // instead).  Endpoint validates ownership server-side, so the list
  // doesn't need to contain the thread for the load to succeed.
  const location = useLocation();
  useEffect(() => {
    const id = (location.hash || '').replace(/^#/, '').trim();
    if (id && id !== expandedId) {
      openThread(id);
    }
    // expandedId omitted on purpose — we only want to react to the
    // URL hash changing, not the internal toggle of openThread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // Fast-poll the open thread so reply + typing land near-realtime.
  useEffect(() => {
    if (!expandedId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await api(`/v1/admin/help/requests/${encodeURIComponent(expandedId)}`);
        if (cancelled) return;
        setActiveThread((prev) => {
          if (!prev) return fresh;
          const replyChanged = (prev.replies?.length || 0) !== (fresh.replies?.length || 0)
            || prev.assignee_id !== fresh.assignee_id
            || prev.status !== fresh.status
            || !!prev.typing_user !== !!fresh.typing_user;
          return replyChanged ? fresh : prev;
        });
        setItems((prev) => prev.map((it) => it.request_id === expandedId ? fresh : it));
      } catch { /* silent */ }
    };
    const t = setInterval(tick, FAST_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [api, expandedId]);

  const openThread = useCallback(async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setActiveThread(null);
      setReplyDraft('');
      return;
    }
    setExpandedId(id);
    setActiveThread(null);
    setReplyDraft('');
    setPromoteOpen(null);
    try {
      const full = await api(`/v1/admin/help/requests/${encodeURIComponent(id)}`);
      setActiveThread(full);
      try {
        await api(`/v1/admin/help/requests/${encodeURIComponent(id)}/read`, { method: 'POST' });
        setItems((prev) => prev.map((it) => it.request_id === id ? { ...it, operator_unread: false } : it));
      } catch { /* non-fatal */ }
    } catch (e) {
      setError(`Couldn't load thread: ${e.message || e}`);
    }
  }, [api, expandedId]);

  const submitReply = useCallback(async (overrideBody = null) => {
    if (!activeThread) return;
    const body = (overrideBody ?? replyDraft).trim();
    if (!body) return;
    setSending(true);
    try {
      const updated = await api(`/v1/admin/help/requests/${encodeURIComponent(activeThread.request_id)}/replies`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      setActiveThread(updated);
      setReplyDraft('');
      setItems((prev) => prev.map((it) => it.request_id === updated.request_id ? updated : it));
    } catch (e) {
      setError(`Couldn't reply: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  }, [api, activeThread, replyDraft]);

  const setStatus = useCallback(async (status) => {
    if (!activeThread) return;
    try {
      const updated = await api(`/v1/admin/help/requests/${encodeURIComponent(activeThread.request_id)}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      setActiveThread(updated);
      setItems((prev) => prev.map((it) => it.request_id === updated.request_id ? updated : it));
    } catch (e) {
      setError(`Couldn't update status: ${e.message || e}`);
    }
  }, [api, activeThread]);

  const claimTicket = useCallback(async () => {
    if (!activeThread) return;
    try {
      const updated = await api(`/v1/admin/help/requests/${encodeURIComponent(activeThread.request_id)}/assign`, {
        method: 'POST',
        body: JSON.stringify({ assignee_id: meId, assignee_email: meEmail, assignee_name: meName }),
      });
      setActiveThread(updated);
      setItems((prev) => prev.map((it) => it.request_id === updated.request_id ? updated : it));
    } catch (e) {
      setError(`Couldn't assign: ${e.message || e}`);
    }
  }, [api, activeThread, meId, meEmail, meName]);

  const unassignTicket = useCallback(async () => {
    if (!activeThread) return;
    try {
      const updated = await api(`/v1/admin/help/requests/${encodeURIComponent(activeThread.request_id)}/assign`, {
        method: 'POST',
        body: JSON.stringify({ assignee_id: '', assignee_email: '', assignee_name: '' }),
      });
      setActiveThread(updated);
      setItems((prev) => prev.map((it) => it.request_id === updated.request_id ? updated : it));
    } catch (e) {
      setError(`Couldn't unassign: ${e.message || e}`);
    }
  }, [api, activeThread]);

  const triage = useCallback(async (patch) => {
    if (!activeThread) return;
    try {
      const updated = await api(`/v1/admin/help/requests/${encodeURIComponent(activeThread.request_id)}/triage`, {
        method: 'POST',
        body: JSON.stringify(patch),
      });
      setActiveThread(updated);
      setItems((prev) => prev.map((it) => it.request_id === updated.request_id ? updated : it));
    } catch (e) {
      setError(`Couldn't update triage: ${e.message || e}`);
    }
  }, [api, activeThread]);

  const pingTyping = useCallback((value) => {
    if (!activeThread) return;
    // Only ping when there's something in the textarea — clearing the
    // box should stop sending pings immediately so the user-side
    // indicator fades naturally on the next poll cycle.  Previously
    // any prior keystroke kept the indicator "stuck on" for the full
    // hold window even after the operator stepped away.
    if (!(value || '').trim()) return;
    const now = Date.now();
    if (now - lastTypingPing.current < TYPING_PING_MS) return;
    lastTypingPing.current = now;
    api(`/v1/admin/help/requests/${encodeURIComponent(activeThread.request_id)}/typing`, { method: 'POST' })
      .catch(() => {});
  }, [api, activeThread]);

  const promoteReply = useCallback(async (reply) => {
    if (!activeThread || !promoteTitle.trim() || !promoteBody.trim()) return;
    try {
      await api(
        `/v1/admin/help/requests/${encodeURIComponent(activeThread.request_id)}/replies/${encodeURIComponent(reply.reply_id)}/promote`,
        {
          method: 'POST',
          body: JSON.stringify({
            title: promoteTitle.trim(),
            routes: promoteRoutes.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
            tags: ['promoted'],
            body: promoteBody.trim(),
          }),
        },
      );
      setPromoteOpen(null);
      setPromoteTitle('');
      setPromoteRoutes('');
      setPromoteBody('');
    } catch (e) {
      setError(`Couldn't promote: ${e.message || e}`);
    }
  }, [api, activeThread, promoteTitle, promoteRoutes, promoteBody]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((r) => {
      // Assignee filter
      if (assigneeFilter === 'mine' && r.assignee_id !== meId) return false;
      if (assigneeFilter === 'unassigned' && r.assignee_id) return false;
      // Status filter
      if (statusFilter !== 'all') {
        const s = (r.status === 'awaiting_user' || r.status === 'resolved') ? r.status : 'open';
        if (s !== statusFilter) return false;
      }
      // Free-text search across the obvious fields
      if (!needle) return true;
      const hay = [
        r.body, r.excerpt, r.subject, r.user_email, r.user_id, r.user_name,
        r.route, r.page_title,
        ...(r.tags || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [items, search, assigneeFilter, statusFilter, meId]);

  // Sorted flat list — priority first (urgent > high > normal > low),
  // then recency.  Used by the master-detail list, replacing the old
  // three-bucket column layout that squashed metadata into ellipses.
  const sortedItems = useMemo(() => {
    const pri = { urgent: 0, high: 1, normal: 2, low: 3 };
    return [...filteredItems].sort((a, b) => {
      // Unread first so newly-arrived threads jump to the top of the
      // list as a visual cue (combined with the .help-card--pulse
      // animation, feedback #6).
      const ua = a.operator_unread ? 0 : 1;
      const ub = b.operator_unread ? 0 : 1;
      if (ua !== ub) return ua - ub;
      const pa = pri[a.priority || 'normal'] ?? 2;
      const pb = pri[b.priority || 'normal'] ?? 2;
      if (pa !== pb) return pa - pb;
      return (b.updated_at || '').localeCompare(a.updated_at || '');
    });
  }, [filteredItems]);

  // Status counts off the search/assignee-filtered set so the pill
  // numbers reflect what's actually about to appear in the list.
  const statusCounts = useMemo(() => {
    const c = { all: 0, open: 0, awaiting_user: 0, resolved: 0 };
    const needle = search.trim().toLowerCase();
    for (const r of items) {
      if (assigneeFilter === 'mine' && r.assignee_id !== meId) continue;
      if (assigneeFilter === 'unassigned' && r.assignee_id) continue;
      if (needle) {
        const hay = [
          r.body, r.excerpt, r.subject, r.user_email, r.user_id, r.user_name,
          r.route, r.page_title, ...(r.tags || []),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) continue;
      }
      c.all += 1;
      const s = (r.status === 'awaiting_user' || r.status === 'resolved') ? r.status : 'open';
      c[s] += 1;
    }
    return c;
  }, [items, search, assigneeFilter, meId]);

  const counts = useMemo(() => ({
    mine: items.filter((r) => r.assignee_id === meId).length,
    unassigned: items.filter((r) => !r.assignee_id).length,
    total: items.length,
  }), [items, meId]);

  // Page-level context actions — one tap each to slice the inbox at
  // the most-pressing thing.  Hide actions that are already what the
  // user's looking at (e.g. "Open mine" when assigneeFilter==mine).
  const ctxActions = useMemo(() => {
    const out = [];
    const stale = items.filter((r) => {
      if (r.status === 'resolved' || !r.created_at) return false;
      return Date.now() - new Date(r.created_at).getTime() >= 86_400_000;
    }).length;
    if (stale > 0) {
      out.push({
        id: 'stale',
        title: stale === 1 ? '1 ticket open over a day' : `${stale} tickets open over a day`,
        body: 'These users haven\'t heard back. Search by tag or assignee to triage.',
        cta: 'Sort by age',
        onClick: () => { setSearch(''); setAssigneeFilter('all'); },
        tone: 'warn',
      });
    }
    if (counts.mine > 0 && assigneeFilter !== 'mine') {
      out.push({
        id: 'mine',
        title: counts.mine === 1 ? '1 ticket assigned to you' : `${counts.mine} tickets assigned to you`,
        body: 'Filter to just your queue and start replying.',
        cta: 'Show mine',
        onClick: () => setAssigneeFilter('mine'),
        tone: 'urgent',
      });
    }
    if (counts.unassigned > 0 && assigneeFilter !== 'unassigned') {
      out.push({
        id: 'unassigned',
        title: counts.unassigned === 1 ? '1 unassigned ticket' : `${counts.unassigned} unassigned tickets`,
        body: 'No-one\'s picked these up yet. Take one to start the clock.',
        cta: 'Show unassigned',
        onClick: () => setAssigneeFilter('unassigned'),
        tone: 'primary',
      });
    }
    return out.slice(0, 3); // keep it short so the inbox stays the focus
  }, [items, counts.mine, counts.unassigned, assigneeFilter]);

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Pinpoint inbox{unread > 0 ? ` (${unread} unread)` : ''}</h1>
          <p className="page__subtitle">
            Conversations from users who clicked the Pinpoint widget on your
            instrumented surface. Open a card to read the thread; replies
            stream in real time.
            <br/>
            Curate the FAQ library at <Link to="/knowledge-base">Knowledge base</Link>,
            or read user ratings + comments at <Link to="/help/feedback">Feedback</Link>.
          </p>
        </div>
      </div>

      <ContextActions actions={ctxActions} />

      {!lockedProjectId && (
        <ProjectFilterBar
          projects={projects}
          active={projectFilter}
          items={items}
          onSelect={setActiveProject}
        />
      )}

      <InboxControls
        search={search}
        setSearch={setSearch}
        assigneeFilter={assigneeFilter}
        setAssigneeFilter={setAssigneeFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        counts={counts}
        statusCounts={statusCounts}
      />

      {stats && <StatsBar stats={stats} />}

      {error && (
        <div className="state state--error">
          <div className="state__title">Couldn't load</div>
          <div>{error}</div>
        </div>
      )}

      {loading && !items.length && (
        <div className="state"><div className="state__title">Loading…</div></div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="state">
          <div className="state__title">No requests yet</div>
          <div>
            When a user clicks the help widget on your product, their
            messages will appear here.  The launcher renders at the
            bottom-right of every SDK-instrumented page by default, or
            you can embed it inline with{' '}
            <code>&lt;div data-spotlight-help-mount&gt;</code>.
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="help-master">
          <aside className="help-master__list" aria-label="Help requests">
            {sortedItems.length === 0 && (
              <div className="help-master__empty">
                Nothing matches the current filter.{' '}
                <button
                  type="button"
                  className="btn btn--text help-master__reset"
                  onClick={() => { setSearch(''); setAssigneeFilter('all'); setStatusFilter('all'); }}
                >
                  Clear filters
                </button>
              </div>
            )}
            {sortedItems.map((r) => (
              <InboxRow
                key={r.request_id}
                request={r}
                active={expandedId === r.request_id}
                onSelect={openThread}
              />
            ))}
          </aside>
          <section className="help-master__detail" aria-label="Selected request">
            {!expandedId && (
              <div className="help-master__placeholder">
                <div className="help-master__placeholder-title">Select a request</div>
                <div className="help-master__placeholder-body">
                  Click a card on the left to read the conversation, reply,
                  and triage. New replies pulse and surface at the top
                  automatically.
                </div>
              </div>
            )}
            {expandedId && (
              activeThread ? (
                <Thread
                  thread={activeThread}
                  draft={replyDraft}
                  setDraft={setReplyDraft}
                  onReply={() => submitReply()}
                  onCanned={(c) => submitReply(c.body)}
                  onResolve={() => setStatus('resolved')}
                  onReopen={() => setStatus('open')}
                  onClaim={claimTicket}
                  onUnassign={unassignTicket}
                  onPing={pingTyping}
                  onClose={() => { setExpandedId(null); setActiveThread(null); setReplyDraft(''); }}
                  sending={sending}
                  promoteOpen={promoteOpen}
                  promoteBody={promoteBody}
                  setPromoteBody={setPromoteBody}
                  setPromoteOpen={setPromoteOpen}
                  promoteTitle={promoteTitle}
                  setPromoteTitle={setPromoteTitle}
                  promoteRoutes={promoteRoutes}
                  setPromoteRoutes={setPromoteRoutes}
                  onPromote={promoteReply}
                  onTriage={triage}
                  meId={meId}
                />
              ) : (
                <div className="help-thread__loading">Loading thread…</div>
              )
            )}
          </section>
        </div>
      )}

      <style>{INBOX_CSS}</style>
    </div>
  );
}

function Thread({
  thread, draft, setDraft, onReply, onCanned, onResolve, onReopen,
  onClaim, onUnassign, onPing, onClose, sending,
  promoteOpen, setPromoteOpen, promoteTitle, setPromoteTitle,
  promoteRoutes, setPromoteRoutes, promoteBody, setPromoteBody,
  onPromote, onTriage, meId,
}) {
  const isMine = thread.assignee_id === meId;
  return (
    <div className="help-thread">
      <div className="help-thread__head">
        {onClose && (
          <button
            type="button"
            className="help-thread__close"
            onClick={onClose}
            title="Close (or pick another request on the left)"
            aria-label="Close request"
          >
            <XIcon strokeWidth={2.5} />
          </button>
        )}
        <code className="help-thread__id">{thread.request_id}</code>
        <span className={`help-status help-status--${thread.status}`}>{humanStatus(thread.status)}</span>
        <PrioritySelector value={thread.priority || 'normal'} onChange={(p) => onTriage({ priority: p })} />
        {thread.assignee_id ? (
          <div className="help-thread__assignee">
            <Avatar name={thread.assignee_name || thread.assignee_email} accent identity={`op:${thread.assignee_id}`} />
            <span>{thread.assignee_name || thread.assignee_email}</span>
            <button
              type="button"
              className="help-thread__assignee-x"
              onClick={onUnassign}
              title="Unassign"
              aria-label="Unassign operator"
            >
              <XIcon size={12} />
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClaim}>
            <UserIcon /> Take this ticket
          </button>
        )}
        <div className="help-thread__head-actions">
          {thread.status === 'resolved'
            ? <button type="button" className="btn btn--secondary" onClick={onReopen}>
                <RefreshIcon /> Re-open
              </button>
            : <button type="button" className="btn btn--secondary" onClick={onResolve}>
                <CheckIcon /> Mark resolved
              </button>
          }
        </div>
      </div>

      <TagEditor tags={thread.tags || []} onChange={(tags) => onTriage({ tags })} />

      <ContextMap thread={thread} />

      <div className="help-thread__messages">
        <div className="help-msg help-msg--user">
          <Avatar
            name={userDisplayName(thread)}
            identity={thread.user_id || thread.user_email}
          />
          <div className="help-msg__col">
            <div className="help-msg__meta">
              <strong>{userDisplayName(thread)}</strong>
              {' · '}
              <span title={new Date(thread.created_at).toLocaleString()}>
                {formatRelative(thread.created_at)}
              </span>
            </div>
            <div className="help-msg__body">{thread.body}</div>
            <MessageAttachments atts={thread.attachments} />
            {thread.context_picks?.length > 0 && (
              <div className="help-msg__picks">
                <div className="help-msg__picks-title">PICKED ELEMENTS</div>
                <ul>
                  {thread.context_picks.map((p, i) => (
                    <li key={i}>
                      <code>{p.name || p.snippet || 'Element'}</code>
                      {p.entity && <> · {p.entity}{p.entity_id ? ` / ${p.entity_id}` : ''}</>}
                      {p.state && <> · state=<code>{p.state}</code></>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {(thread.replies || []).map((r) => {
          const isOp = r.author_kind === 'operator';
          // For user replies, fall back to the parent thread's
          // identity so the avatar disc stays the same colour and
          // the initials don't flip from "D" (email) to "U" (literal
          // "User" fallback) across rows.
          const name = isOp
            ? (r.author_name || r.author_email || 'Support')
            : (r.author_name || r.author_email || userDisplayName(thread));
          const identity = isOp
            ? `op:${r.author_id || r.author_email || 'support'}`
            : `user:${thread.user_id || thread.user_email}`;
          return (
          <div key={r.reply_id} className={`help-msg help-msg--${isOp ? 'op' : 'user'}`}>
            <Avatar name={name} identity={identity} accent={isOp} />
            <div className="help-msg__col">
              <div className="help-msg__meta">
                <strong>{name}</strong>
                {' · '}
                <span title={new Date(r.created_at).toLocaleString()}>{formatRelative(r.created_at)}</span>
              </div>
              <div className="help-msg__body">{r.body}</div>
              <MessageAttachments atts={r.attachments} />
              {isOp && <ReadReceipt reply={r} />}
              {r.author_kind === 'operator' && (
                <div className="help-msg__row-actions">
                  {promoteOpen === r.reply_id ? (
                    <div className="help-promote">
                      <div className="help-promote__hint">
                        This becomes a public article in the help widget&apos;s
                        Quick Answers. Edit before saving — drop user names,
                        polish the wording, fix any typos.
                      </div>
                      <input
                        className="help-promote__input"
                        placeholder="Article title (e.g. 'How to export orders')"
                        value={promoteTitle}
                        onChange={(e) => setPromoteTitle(e.target.value)}
                      />
                      <textarea
                        className="help-promote__body"
                        rows={5}
                        placeholder="Article body — what every user should see when they open this question."
                        value={promoteBody}
                        onChange={(e) => setPromoteBody(e.target.value)}
                      />
                      <input
                        className="help-promote__input"
                        placeholder="Routes (comma — leave blank for everywhere)"
                        value={promoteRoutes}
                        onChange={(e) => setPromoteRoutes(e.target.value)}
                      />
                      <div className="help-promote__actions">
                        <button type="button" className="btn btn--text" onClick={() => setPromoteOpen(null)}>
                          <XIcon /> Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          onClick={() => onPromote(r)}
                          disabled={!promoteTitle.trim() || !promoteBody.trim()}
                        >
                          <AddIcon /> Save as article
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="help-msg__promote"
                      onClick={() => {
                        setPromoteOpen(r.reply_id);
                        setPromoteTitle('');
                        setPromoteRoutes('');
                        // Pre-fill body from the reply so the operator has
                        // something to edit, not blank cursor anxiety.
                        setPromoteBody(r.body || '');
                      }}
                    >
                      Promote to FAQ <ArrowRightIcon />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          );
        })}

        {thread.typing_user && (
          <div className="help-msg help-msg--user help-msg--typing">
            <Avatar name={userDisplayName(thread)} identity={thread.user_id || thread.user_email} />
            <div className="help-msg__col">
              <div className="help-msg__meta"><em>typing…</em></div>
              <div className="help-msg__body help-msg__body--typing"><span></span><span></span><span></span></div>
            </div>
          </div>
        )}
      </div>

      <div className="help-thread__canned">
        {CANNED_RESPONSES.map((c) => (
          <button
            key={c.id}
            type="button"
            className="help-canned-btn"
            onClick={() => onCanned(c)}
            disabled={sending}
            title={c.body}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="help-thread__reply">
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); onPing(e.target.value); }}
          placeholder={isMine ? 'Reply to the user…' : 'Reply (or take the ticket above first to assign yourself)'}
        />
        <div className="help-thread__reply-actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={onReply}
            disabled={sending || !draft.trim()}
          >
            <SendIcon /> {sending ? 'Sending…' : 'Reply'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContextMap({ thread }) {
  const ctx = thread?.client_context || {};
  const groups = Object.entries(ctx).filter(([, v]) =>
    v && (typeof v !== 'object' || Object.keys(v).length > 0)
  );
  // Synthesise a "page" group from page-level fields.
  const pageGroup = {
    route: thread.route,
    url: thread.page_url,
    title: thread.page_title,
  };
  const pageEntries = Object.entries(pageGroup).filter(([, v]) => v);
  if (groups.length === 0 && pageEntries.length === 0) return null;
  return (
    <div className="help-context-map">
      <div className="help-context-map__title">Context map</div>
      {pageEntries.length > 0 && (
        <ContextGroup label="page" rows={pageEntries} />
      )}
      {groups.map(([label, val]) => {
        const rows = (typeof val === 'object' && val !== null)
          ? Object.entries(val)
          : [['value', val]];
        return <ContextGroup key={label} label={label} rows={rows} />;
      })}
    </div>
  );
}

function ContextGroup({ label, rows }) {
  return (
    <div className="help-context-group">
      <div className="help-context-group__label">{label}</div>
      <dl className="help-context-group__dl">
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <dt>{k}</dt>
            <dd>{renderCtxValue(v)}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

function renderCtxValue(v) {
  if (v == null) return <span className="help-context-empty">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="help-context-empty">[]</span>;
    return <span>{v.map((x, i) => <code key={i} className="help-context-tag">{String(x)}</code>)}</span>;
  }
  if (typeof v === 'object') return <code className="help-context-code">{JSON.stringify(v)}</code>;
  return <code className="help-context-code">{String(v)}</code>;
}

/** Search + assignee filter + status filter row. Sits between project
 *  pills and the stats bar — gives the operator one-keystroke triage
 *  on a busy day without re-reading every card. Status pills replaced
 *  the old three-column layout (feedback #4). */
function InboxControls({
  search, setSearch,
  assigneeFilter, setAssigneeFilter,
  statusFilter, setStatusFilter,
  counts, statusCounts,
}) {
  return (
    <div className="help-controls">
      <input
        type="search"
        className="help-controls__search"
        placeholder="Search messages, users, routes, tags…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search the inbox"
      />
      <div className="help-controls__filters" role="tablist" aria-label="Assignee filter">
        <button
          type="button" role="tab"
          aria-selected={assigneeFilter === 'all'}
          className={'pill ' + (assigneeFilter === 'all' ? 'pill--active' : '')}
          onClick={() => setAssigneeFilter('all')}
        >
          All <span className="help-controls__count">{counts.total}</span>
        </button>
        <button
          type="button" role="tab"
          aria-selected={assigneeFilter === 'mine'}
          className={'pill ' + (assigneeFilter === 'mine' ? 'pill--active' : '')}
          onClick={() => setAssigneeFilter('mine')}
        >
          Mine <span className="help-controls__count">{counts.mine}</span>
        </button>
        <button
          type="button" role="tab"
          aria-selected={assigneeFilter === 'unassigned'}
          className={'pill ' + (assigneeFilter === 'unassigned' ? 'pill--active' : '')}
          onClick={() => setAssigneeFilter('unassigned')}
        >
          Unassigned <span className="help-controls__count">{counts.unassigned}</span>
        </button>
      </div>
      <div className="help-controls__divider" aria-hidden />
      <div className="help-controls__filters" role="tablist" aria-label="Status filter">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.id}
            type="button" role="tab"
            aria-selected={statusFilter === s.id}
            title={s.hint || ''}
            className={'pill ' + (statusFilter === s.id ? 'pill--active' : '')}
            onClick={() => setStatusFilter(s.id)}
          >
            {s.label}
            <span className="help-controls__count">{statusCounts[s.id] ?? 0}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** A single row in the master list — shows enough to triage without
 *  selecting (sender, excerpt, route, age, unread/reply count).
 *  Cards with the operator-unread flag pulse to draw attention to
 *  fresh replies (feedback #6).  Defined as a memoised component so
 *  the 3 s thread poll only re-renders the selected row, not every
 *  card in the list. */
const InboxRow = function InboxRow({ request: r, active, onSelect }) {
  const replyCount = r.reply_count || 0;
  // The list endpoint doesn't expand replies (reply_count is the only
  // signal of count), so use last_actor + operator_unread as the "new
  // user reply" cue.  Either an unread row OR one whose last actor was
  // the user qualifies — the inbox is supposed to draw the eye to
  // anything waiting on the operator.
  const hasUserReply = (r.last_actor === 'user') && (replyCount > 0 || r.operator_unread);
  const className = [
    'help-row',
    active ? 'help-row--active' : '',
    r.operator_unread ? 'help-row--unread' : '',
    hasUserReply && !active ? 'help-row--pulse' : '',
  ].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      data-ui-name="help-inbox-row"
      className={className}
      onClick={() => onSelect(r.request_id)}
    >
      <div className="help-row__avatar">
        <Avatar
          name={r.user_name || r.user_email || r.user_id || 'User'}
          identity={r.user_id || r.user_email}
        />
        {r.operator_unread && <span className="help-row__dot" aria-label="Unread" />}
      </div>
      <div className="help-row__body">
        <div className="help-row__line1">
          <span className="help-row__from">
            {r.user_name || (r.user_email || '').split('@')[0] || r.user_id || 'Guest'}
          </span>
          <span className={'help-row__status help-row__status--' + (r.status || 'open')}>
            {humanStatus(r.status || 'open')}
          </span>
          <span className={'help-row__time ' + ageClass(r)}
                title={r.created_at ? new Date(r.created_at).toLocaleString() : ''}>
            {formatRelative(r.updated_at)}
          </span>
        </div>
        <div className="help-row__excerpt">{r.excerpt || r.body || '(no body)'}</div>
        <div className="help-row__line3">
          {r.priority && r.priority !== 'normal' && (
            <span className={`help-row__pri help-row__pri--${r.priority}`}>{r.priority}</span>
          )}
          {replyCount > 0 && (
            <span
              className={'help-row__replies ' + (hasUserReply && !active ? 'help-row__replies--new' : '')}
              title={hasUserReply ? 'New reply from user' : `${replyCount} repl${replyCount === 1 ? 'y' : 'ies'}`}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              {replyCount}
            </span>
          )}
          {r.rating > 0 && (
            <span className="help-row__rating" title={r.rating_comment || ''}>
              {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
            </span>
          )}
          <span className="help-row__route" title={r.page_url || r.route}>
            {r.page_title || r.route || '/'}
          </span>
          {r.assignee_id && (
            <span className="help-row__assignee" title={r.assignee_name || r.assignee_email}>
              <Avatar
                name={r.assignee_name || r.assignee_email}
                identity={`op:${r.assignee_id}`}
                accent
              />
              <span className="help-row__assignee-name">
                {r.assignee_name || (r.assignee_email || '').split('@')[0]}
              </span>
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

/** Headline stats + per-operator leaderboard for the current window.
 *
 *  Surfaces MTTFR / MTTR / volume on the left and the top operators
 *  (by tickets-resolved-then-replies) on the right. Designed to fit
 *  in a single horizontal strip so it doesn't push the inbox off the
 *  fold; the leaderboard wraps below at narrow widths.
 */
function StatsBar({ stats }) {
  const s = stats?.data || stats;
  if (!s) return null;
  const ops = (s.operators || []).slice(0, 5);
  return (
    <div className="help-stats">
      <div className="help-stats__metrics" role="group" aria-label="Help-desk performance">
        <StatMetric label="Tickets" value={s.total} sub={`last ${s.window_days}d`} />
        <StatMetric label="Open" value={s.open} accent={s.open > 0 ? 'amber' : 'neutral'} />
        <StatMetric label="Resolved" value={s.resolved} accent="green" />
        <StatMetric
          label="MTTFR"
          value={formatDuration(s.avg_first_response_seconds)}
          sub="mean first response"
        />
        <StatMetric
          label="MTTR"
          value={formatDuration(s.avg_resolution_seconds)}
          sub="mean to resolution"
        />
        <StatMetric
          label="P90 resolve"
          value={formatDuration(s.p90_resolution_seconds)}
          sub="slowest 10%"
        />
        <StatMetric
          label="CSAT"
          value={s.csat_percent != null ? `${s.csat_percent.toFixed(0)}%` : '—'}
          sub={s.rated > 0 ? `${s.rated} rated · avg ${s.avg_rating?.toFixed(1)}★` : 'no ratings yet'}
          accent={s.csat_percent != null && s.csat_percent >= 80 ? 'green' : 'neutral'}
        />
      </div>
      {ops.length > 0 && (
        <div className="help-stats__leaderboard" aria-label="Top operators">
          <div className="help-stats__lead-title">Top operators</div>
          <ol className="help-stats__lead-list">
            {ops.map((o) => (
              <li key={o.operator_id} className="help-stats__lead-row">
                <Avatar name={o.operator_name || o.operator_email || o.operator_id} accent />
                <div className="help-stats__lead-name">
                  <strong>{o.operator_name || o.operator_email || o.operator_id || 'Unassigned'}</strong>
                  <span className="help-stats__lead-sub">
                    {o.resolved} resolved · {o.replied} replies
                    {o.avg_resolution_seconds != null && ` · MTTR ${formatDuration(o.avg_resolution_seconds)}`}
                    {o.rated_count > 0 && o.avg_rating != null && ` · ${o.avg_rating.toFixed(1)}★ (${o.rated_count})`}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function StatMetric({ label, value, sub, accent = 'neutral' }) {
  return (
    <div className={'help-stat help-stat--' + accent}>
      <div className="help-stat__value">{value ?? '—'}</div>
      <div className="help-stat__label">{label}</div>
      {sub && <div className="help-stat__sub">{sub}</div>}
    </div>
  );
}

function formatDuration(secs) {
  if (secs == null) return '—';
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = secs / 60;
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(h < 10 ? 1 : 0)}h`;
  const d = h / 24;
  return `${d.toFixed(d < 10 ? 1 : 0)}d`;
}

const PRIORITY_OPTIONS = [
  { id: 'urgent', label: 'Urgent', color: '#dc2626' },
  { id: 'high',   label: 'High',   color: '#ea580c' },
  { id: 'normal', label: 'Normal', color: '#475569' },
  { id: 'low',    label: 'Low',    color: '#94a3b8' },
];

function PrioritySelector({ value, onChange }) {
  // Native <select> over a popover — the goal is "type-1 click, done"
  // for triage, and the inbox header is dense enough that a custom
  // listbox would dwarf the actual conversation.
  return (
    <label className={'help-priority help-priority--' + value} title={`Priority: ${value}`}>
      <span className="help-priority__dot" />
      <select
        className="help-priority__select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Priority"
      >
        {PRIORITY_OPTIONS.map((p) => (
          <option key={p.id} value={p.id}>{p.label}</option>
        ))}
      </select>
    </label>
  );
}

function TagEditor({ tags, onChange }) {
  const [draft, setDraft] = useState('');
  function addTag() {
    const t = draft.trim().toLowerCase();
    if (!t || tags.includes(t) || tags.length >= 8) {
      setDraft('');
      return;
    }
    onChange([...tags, t]);
    setDraft('');
  }
  function removeTag(t) {
    onChange(tags.filter((x) => x !== t));
  }
  return (
    <div className="help-tags">
      {tags.map((t) => (
        <span key={t} className="help-tag">
          {t}
          <button
            type="button"
            className="help-tag__x"
            onClick={() => removeTag(t)}
            aria-label={`Remove ${t} tag`}
            title={`Remove ${t} tag`}
          >
            <XIcon size={10} />
          </button>
        </span>
      ))}
      <input
        type="text"
        className="help-tags__input"
        placeholder={tags.length === 0 ? 'Add tag (bug, billing, …)' : 'Add another'}
        value={draft}
        maxLength={40}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); addTag(); }
          if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
            removeTag(tags[tags.length - 1]);
          }
        }}
        onBlur={addTag}
      />
    </div>
  );
}

/** Per-project filter row.
 *
 *  Shows the projects this operator has access to (server filters
 *  the inbox list by their `admin_user.project_ids`).  Counts come
 *  from the currently-loaded list so they reflect the active status
 *  filter / search.  "All" returns to the operator's full scope.
 */
function ProjectFilterBar({ projects, active, items, onSelect }) {
  // Build the visible chip set from BOTH the project list (so empty
  // projects still appear) and the loaded items (so a request from a
  // project the operator hasn't been given a row for — e.g. an
  // orphan project_id — still shows up).
  const counts = new Map();
  for (const r of items || []) {
    const pid = r.project_id || '';
    counts.set(pid, (counts.get(pid) || 0) + 1);
  }
  const chips = [];
  for (const p of projects || []) {
    chips.push({
      id: p.project_id,
      label: p.name || p.slug || p.project_id,
      count: counts.get(p.project_id) || 0,
    });
  }
  // Tenant-wide threads (no project_id) — surface as their own pill so
  // operators know which ones came from un-scoped API keys.
  if ((counts.get('') || 0) > 0) {
    chips.push({ id: '__tenant__', label: 'Tenant-wide', count: counts.get('') || 0, disabled: true });
  }
  if (chips.length === 0) return null;
  return (
    <div className="help-projbar" role="tablist" aria-label="Filter by project">
      <button
        type="button"
        role="tab"
        aria-selected={!active}
        className={'help-projbar__chip ' + (!active ? 'help-projbar__chip--active' : '')}
        onClick={() => onSelect(null)}
      >
        All projects
        <span className="help-projbar__count">{(items || []).length}</span>
      </button>
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          role="tab"
          aria-selected={active === c.id}
          disabled={c.disabled}
          title={c.disabled ? 'Tenant-wide requests (no project scope) — always visible.' : ''}
          className={
            'help-projbar__chip '
            + (active === c.id ? 'help-projbar__chip--active ' : '')
            + (c.disabled ? 'help-projbar__chip--passive' : '')
          }
          onClick={() => !c.disabled && onSelect(c.id)}
        >
          {c.label}
          <span className="help-projbar__count">{c.count}</span>
        </button>
      ))}
    </div>
  );
}

/** Render screenshot attachments inline in a message bubble.
 *  Click thumbnail → opens full-size in a new tab.  Backend returns a
 *  relative path so the same shape works in every environment; we
 *  prepend the dashboard's API base here. */
function MessageAttachments({ atts }) {
  const list = atts || [];
  if (list.length === 0) return null;
  const base = resolveApiBase();
  const href = (u) => (u && /^https?:\/\//i.test(u) ? u : `${base}${u || ''}`);
  return (
    <div className="help-msg-atts">
      {list.map((a) => {
        if (!a.url) {
          return (
            <div key={a.attachment_id} className="help-msg-att help-msg-att--pending" title="Pending virus scan">
              Scanning…
            </div>
          );
        }
        const url = href(a.url);
        return (
          <a
            key={a.attachment_id}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="help-msg-att"
            title={`${a.filename || 'Screenshot'} · ${a.content_type} · ${Math.round((a.size || 0) / 1024)} KB`}
          >
            <img src={url} alt={a.filename || 'screenshot'} loading="lazy" />
          </a>
        );
      })}
    </div>
  );
}

/** Operator-side read receipt under each reply.
 *
 *  Three states, mirroring iMessage / WhatsApp semantics so users
 *  read them at a glance:
 *    - Sending    — POST in flight (single hollow ring)
 *    - Sent       — reply persisted but user hasn't been online since
 *    - Delivered  — user's widget fetched this reply
 *    - Read       — user's widget marked the reply read (opened the
 *                   panel while it was visible, or scrolled it into
 *                   view).
 *
 *  Backend may expose ``delivered_at`` / ``read_at`` on each reply
 *  (added in the help-replies repo).  Missing fields degrade to
 *  ``Sent`` so older rows stay readable.
 */
function ReadReceipt({ reply }) {
  const readAt = reply?.read_at;
  const deliveredAt = reply?.delivered_at;
  let state = 'sent';
  let label = 'Sent';
  let title = reply?.created_at ? `Sent ${new Date(reply.created_at).toLocaleString()}` : 'Sent';
  if (readAt) {
    state = 'read';
    label = 'Read';
    title = `Read ${new Date(readAt).toLocaleString()}`;
  } else if (deliveredAt) {
    state = 'delivered';
    label = 'Delivered';
    title = `Delivered ${new Date(deliveredAt).toLocaleString()}`;
  }
  return (
    <div className={'help-msg__receipt help-msg__receipt--' + state} title={title}>
      <span className="help-msg__receipt-icon" aria-hidden>
        {state === 'read' || state === 'delivered' ? (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="2 12 8 18 14 12" />
            <polyline points="10 12 16 18 22 6" />
          </svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 12 10 18 20 6" />
          </svg>
        )}
      </span>
      <span className="help-msg__receipt-text">{label}</span>
    </div>
  );
}

/** Display avatar.
 *
 *  ``name`` drives the initials (one-or-two letters from word
 *  boundaries — "Jane Doe" → "JD", "demo@acme.com" → "D").
 *  ``identity`` (optional) drives the hue — same identity always
 *  hashes to the same colour, so every bubble from the *same
 *  person* shows the same disc even when the display name varies
 *  across rows ("D" from the email, "U" from a "User" fallback,
 *  "DU" from a parsed full name).  Falls back to ``name`` when no
 *  identity is supplied so this stays a drop-in replacement.
 */
function Avatar({ name, identity, accent = false }) {
  const display = (name || '?').trim();
  const initials = display.split(/[\s@._-]+/).filter(Boolean).slice(0, 2)
    .map((n) => n[0].toUpperCase()).join('') || '?';
  const hueKey = identity || display;
  let h = 0;
  for (let i = 0; i < hueKey.length; i++) h = (h * 31 + hueKey.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  const bg = accent ? 'var(--brand-600, #4f46e5)' : `hsl(${hue}, 36%, 52%)`;
  return <span className="help-avatar" style={{ background: bg }}>{initials}</span>;
}

/** Pick the best display name for the end user across all thread
 *  surfaces so the same person gets the same initials + identity
 *  hue every time.  Prefer the explicit name; fall back to the
 *  local-part of their email (so "demo@acme.com" → "demo" instead
 *  of just "D"); ultimately the truncated user id. */
function userDisplayName(thread) {
  if (!thread) return 'Guest';
  if (thread.user_name && thread.user_name.trim()) return thread.user_name.trim();
  if (thread.user_email && thread.user_email.trim()) {
    const local = thread.user_email.split('@')[0] || thread.user_email;
    return local.trim();
  }
  if (thread.user_id) return thread.user_id.slice(0, 12);
  return 'Guest';
}

function humanStatus(s) {
  if (s === 'open') return 'Open';
  if (s === 'awaiting_user') return 'Awaiting reply';
  if (s === 'resolved') return 'Resolved';
  return s;
}

/** Highlight stale tickets — anything older than a day on Open/Awaiting
 *  gets the amber tint; older than 3 days reads red.  Resolved cards
 *  stay neutral. */
function ageClass(r) {
  if (!r || r.status === 'resolved' || !r.created_at) return 'help-card__age';
  const ageMs = Date.now() - new Date(r.created_at).getTime();
  const days = ageMs / 86_400_000;
  if (days >= 3) return 'help-card__age help-card__age--bad';
  if (days >= 1) return 'help-card__age help-card__age--warn';
  return 'help-card__age';
}

function formatRelative(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const sec = Math.round(diff / 1000);
    if (sec < 60) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.round(hr / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
}

const INBOX_CSS = `
  /* Stats bar — headline metrics + leaderboard. */
  .help-stats {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 18px;
    align-items: stretch;
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 12px;
    padding: 14px 16px;
    margin: 0 0 14px;
  }
  @media (max-width: 980px) {
    .help-stats { grid-template-columns: 1fr; }
  }
  .help-stats__metrics {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 12px;
  }
  @media (max-width: 720px) {
    .help-stats__metrics { grid-template-columns: repeat(3, 1fr); }
  }
  .help-stat {
    display: flex; flex-direction: column; gap: 2px;
    padding: 8px 10px; border-radius: 8px;
    background: var(--surface-1, #f8fafc);
  }
  .help-stat--amber { background: #fef3c7; }
  .help-stat--green { background: #dcfce7; }
  .help-stat__value { font-size: 18px; font-weight: 700; color: var(--text-1, #0f172a); line-height: 1.15; }
  .help-stat__label { font-size: 11.5px; font-weight: 600; color: var(--text-2, #334155); text-transform: uppercase; letter-spacing: 0.04em; }
  .help-stat__sub { font-size: 11px; color: var(--text-3, #64748b); }
  .help-stats__leaderboard {
    min-width: 220px;
    border-left: 1px solid var(--border-1, #e5e7eb);
    padding-left: 16px;
    display: flex; flex-direction: column; gap: 6px;
  }
  @media (max-width: 980px) {
    .help-stats__leaderboard { border-left: 0; border-top: 1px solid var(--border-1, #e5e7eb); padding: 12px 0 0; }
  }
  .help-stats__lead-title { font-size: 11.5px; font-weight: 600; color: var(--text-2, #334155); text-transform: uppercase; letter-spacing: 0.04em; }
  .help-stats__lead-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
  .help-stats__lead-row { display: flex; align-items: center; gap: 8px; font-size: 12.5px; }
  .help-stats__lead-name { display: flex; flex-direction: column; line-height: 1.25; }
  .help-stats__lead-sub { color: var(--text-3, #64748b); font-size: 11px; }

  /* Priority pill — colour-coded select inline in the thread header. */
  .help-priority {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 2px 4px 2px 8px;
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 9999px;
    background: var(--surface-0, #fff);
    cursor: pointer;
  }
  .help-priority__dot {
    width: 8px; height: 8px; border-radius: 50%; background: #94a3b8;
  }
  .help-priority--urgent .help-priority__dot { background: #dc2626; }
  .help-priority--high   .help-priority__dot { background: #ea580c; }
  .help-priority--normal .help-priority__dot { background: #475569; }
  .help-priority--low    .help-priority__dot { background: #94a3b8; }
  .help-priority--urgent { border-color: #fecaca; background: #fef2f2; }
  .help-priority--high   { border-color: #fed7aa; background: #fff7ed; }
  .help-priority__select {
    appearance: none; background: transparent; border: 0;
    font: inherit; font-size: 11.5px; font-weight: 600; color: var(--text-1, #0f172a);
    padding: 2px 18px 2px 2px; cursor: pointer;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
    background-repeat: no-repeat; background-position: right 4px center;
  }

  /* Tag editor — wrap chips inline with the composer input. */
  .help-tags {
    display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
    margin: 0 0 10px;
    padding: 6px 10px;
    border: 1px dashed var(--border-1, #e5e7eb);
    border-radius: 8px;
    min-height: 32px;
  }
  .help-tag {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 4px 2px 8px;
    background: var(--brand-50, #eef2ff); color: var(--brand-700, #4338ca);
    border-radius: 9999px;
    font-size: 11.5px; font-weight: 600;
  }
  .help-tag__x {
    border: 0; background: transparent; cursor: pointer;
    color: inherit; font-size: 13px; padding: 0 4px;
    line-height: 1;
  }
  .help-tags__input {
    border: 0; outline: 0; font: inherit; font-size: 12.5px;
    flex: 1; min-width: 120px; background: transparent;
  }

  .help-projbar {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: -6px 0 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border-1, #e5e7eb);
  }
  .help-projbar__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 10px 5px 12px;
    border: 1px solid var(--border-1, #e5e7eb);
    background: var(--surface-0, #fff);
    color: var(--text-2, #334155);
    border-radius: 9999px;
    font: inherit;
    font-size: 12.5px;
    cursor: pointer;
    transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
  }
  .help-projbar__chip:hover:not(:disabled) { border-color: var(--brand-600, #4f46e5); color: var(--text-1, #0f172a); }
  .help-projbar__chip--active { background: var(--brand-600, #4f46e5); border-color: var(--brand-600, #4f46e5); color: #fff; }
  .help-projbar__chip--active .help-projbar__count { background: rgba(255,255,255,0.22); color: #fff; }
  .help-projbar__chip--passive { cursor: default; color: var(--text-3, #64748b); }
  .help-projbar__count {
    background: var(--surface-1, #f1f5f9);
    color: var(--text-3, #475569);
    border-radius: 9999px;
    padding: 0 7px;
    font-size: 10.5px;
    font-weight: 700;
    line-height: 18px;
    min-width: 18px;
    text-align: center;
  }

  /* Master-detail layout: list on the left, thread on the right.
     Replaces the old 3-column status board (cards were getting
     squashed on every laptop width).
     The list column is wide enough (440px) that the chip row breathes
     instead of wrapping every other tag.  At <= 1100px the layout
     stacks: list across the top, thread below — better than a 280px
     squashed sidebar.  */
  .help-master {
    display: grid;
    grid-template-columns: minmax(380px, 440px) minmax(0, 1fr);
    gap: 20px;
    align-items: start;
    min-height: 540px;
  }
  @media (max-width: 1100px) {
    .help-master { grid-template-columns: 1fr; }
    .help-master__list { max-height: 56vh; overflow-y: auto; }
  }
  .help-master__list {
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: var(--r-lg, 12px);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: calc(100vh - 280px);
    overflow-y: auto;
  }
  .help-master__detail { display: flex; flex-direction: column; min-width: 0; }
  .help-master__empty { padding: 32px 12px; text-align: center; color: var(--text-3, #64748b); font-size: 13px; }
  .help-master__reset { margin-left: 6px; }
  .help-master__placeholder {
    background: var(--surface-0, #fff);
    border: 1px dashed var(--border-1, #cbd5e1);
    border-radius: var(--r-lg, 12px);
    padding: 56px 24px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: var(--text-3, #64748b);
  }
  .help-master__placeholder-title { font-size: 15px; font-weight: 600; color: var(--text-2, #334155); }
  .help-master__placeholder-body { font-size: 13px; line-height: 1.6; max-width: 380px; margin: 0 auto; }

  /* Inbox row — generously spaced, two visual lines that read at a
     glance:
       line 1 — sender (bigger), the trailing chips (status + age)
       line 2 — full 2-line excerpt + route inline at the end
     Less-essential signals (priority badge, rating stars, assignee
     disc) tuck into a small bottom strip rather than competing with
     the excerpt for space. */
  .help-row {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 12px;
    width: 100%;
    text-align: left;
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 10px;
    padding: 14px 16px;
    cursor: pointer;
    font: inherit;
    color: inherit;
    transition: background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
    position: relative;
  }
  .help-row:hover {
    border-color: var(--brand-200, #c7d2fe);
    box-shadow: 0 4px 12px -8px rgba(15, 23, 42, 0.15);
  }
  .help-row--active {
    background: var(--brand-50, #eef2ff);
    border-color: var(--brand-600, #4f46e5);
    box-shadow: 0 0 0 1px var(--brand-600, #4f46e5);
  }
  .help-row--unread .help-row__from { font-weight: 700; color: var(--text-1, #0f172a); }
  .help-row--unread .help-row__excerpt { color: var(--text-1, #0f172a); }
  .help-row__avatar { position: relative; padding-top: 2px; }
  .help-row__avatar .help-avatar { width: 38px; height: 38px; font-size: 13px; }
  .help-row__dot {
    position: absolute;
    top: 0; right: -2px;
    width: 10px; height: 10px;
    background: var(--brand-600, #4f46e5);
    border: 2px solid var(--surface-0, #fff);
    border-radius: 50%;
  }
  .help-row__body { min-width: 0; display: flex; flex-direction: column; gap: 6px; }
  .help-row__line1 {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }
  .help-row__from {
    font-size: 14px;
    color: var(--text-1, #0f172a);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1;
  }
  .help-row__time {
    font-size: 11.5px;
    color: var(--text-3, #64748b);
    flex-shrink: 0;
  }
  .help-row__status {
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
  }
  .help-row__status--open          { background: #fef3c7; color: #92400e; }
  .help-row__status--awaiting_user { background: #dbeafe; color: #1e40af; }
  .help-row__status--resolved      { background: #dcfce7; color: #166534; }
  .help-row__excerpt {
    font-size: 13px;
    color: var(--text-2, #334155);
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }
  .help-row__line3 {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    margin-top: 2px;
    font-size: 11.5px;
    color: var(--text-3, #64748b);
  }
  .help-row__pri {
    padding: 1px 8px;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .help-row__pri--urgent { background: #fee2e2; color: #b91c1c; }
  .help-row__pri--high   { background: #ffedd5; color: #c2410c; }
  .help-row__pri--low    { background: #f1f5f9; color: #64748b; }
  .help-row__route {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 50%;
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  .help-row__route::before {
    content: '';
    display: inline-block;
    width: 4px; height: 4px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.5;
    margin-right: 3px;
  }
  .help-row__replies {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
  }
  .help-row__replies--new {
    color: var(--brand-600, #4f46e5);
  }
  .help-row__rating { color: #f59e0b; letter-spacing: 0.5px; font-size: 12px; }
  .help-row__assignee { margin-left: auto; display: inline-flex; align-items: center; gap: 5px; }
  .help-row__assignee .help-avatar { width: 20px; height: 20px; font-size: 10px; }
  .help-row__assignee-name {
    color: var(--text-3, #64748b);
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .help-row__time--warn { color: #b45309; font-weight: 600; }
  .help-row__time--bad  { color: #b91c1c; font-weight: 700; }

  /* Pulse — draws the eye to threads where the user just replied
     and is waiting for a response (feedback #6). */
  .help-row--pulse {
    border-color: var(--brand-600, #4f46e5);
    animation: help-row-pulse 2.4s ease-in-out infinite;
  }
  @keyframes help-row-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0); }
    35%      { box-shadow: 0 0 0 5px rgba(79, 70, 229, 0.20); }
    70%      { box-shadow: 0 0 0 0 rgba(79, 70, 229, 0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .help-row--pulse { animation: none; }
  }

  /* Attachment thumbnails inside a thread message. */
  .help-msg-atts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
  .help-msg-att {
    display: block;
    max-width: 200px; max-height: 140px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--border-1, #e5e7eb);
    line-height: 0;
  }
  .help-msg-att img { display: block; max-width: 100%; max-height: 140px; object-fit: cover; }
  .help-msg-att--pending {
    display: inline-flex; align-items: center; justify-content: center;
    padding: 16px 22px;
    font-size: 12px; color: var(--text-3, #64748b);
    background: var(--surface-1, #f1f5f9);
    font-style: italic;
  }

  /* Read receipt — small dim glyph + label under operator bubbles. */
  .help-msg__receipt {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 3px;
    font-size: 10.5px;
    line-height: 1.2;
    color: var(--text-3, #94a3b8);
    user-select: none;
  }
  .help-msg__receipt-icon { display: inline-flex; }
  .help-msg__receipt--read { color: var(--brand-600, #4f46e5); font-weight: 600; }
  .help-msg__receipt--delivered { color: var(--text-2, #475569); }
  .help-msg--op .help-msg__receipt { align-self: flex-end; }

  /* Search + assignee filter row. */
  .help-controls {
    display: flex;
    gap: 12px;
    align-items: center;
    margin: 0 0 14px;
    flex-wrap: wrap;
  }
  .help-controls__search {
    flex: 1;
    min-width: 240px;
    padding: 8px 12px;
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 8px;
    font: inherit;
    font-size: 13px;
    background: var(--surface-0, #fff);
  }
  .help-controls__search:focus { outline: 2px solid var(--brand-600, #4f46e5); outline-offset: 1px; border-color: var(--brand-600, #4f46e5); }
  .help-controls__filters { display: flex; gap: 6px; flex-wrap: wrap; }
  .help-controls__divider {
    width: 1px;
    height: 22px;
    background: var(--border-1, #e5e7eb);
  }
  @media (max-width: 720px) {
    .help-controls__divider { display: none; }
  }
  .help-controls__count {
    margin-left: 6px;
    background: rgba(0,0,0,0.06);
    border-radius: 9999px;
    padding: 0 7px;
    font-size: 10.5px;
    font-weight: 700;
  }
  .pill--active .help-controls__count { background: rgba(255,255,255,0.22); }

  .help-thread { background: var(--surface-1, #f8fafc); border: 1px solid var(--border-1, #e5e7eb); border-radius: 10px; padding: 12px; }
  .help-thread__head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
  .help-thread__close {
    width: 24px; height: 24px;
    border: 0; background: transparent;
    border-radius: 6px;
    color: var(--text-3, #64748b);
    cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .help-thread__close:hover { background: var(--surface-0, #fff); color: var(--text-1, #0f172a); }
  .help-thread__id { font-size: 11px; color: var(--text-3, #64748b); }
  .help-thread__head-actions { margin-left: auto; }
  .help-thread__assignee {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px 3px 4px;
    background: var(--brand-50, #eef2ff);
    border-radius: 9999px;
    font-size: 12px;
    color: var(--text-1, #0f172a);
  }
  .help-thread__assignee-x { background: transparent; border: none; color: var(--text-3, #64748b); cursor: pointer; font-size: 14px; line-height: 1; padding: 0 2px; }
  .help-thread__assignee-x:hover { color: var(--text-1); }
  .help-thread__loading { text-align: center; color: var(--text-3, #64748b); padding: 20px 0; }
  .help-thread__messages { display: flex; flex-direction: column; gap: 12px; margin-bottom: 12px; }

  .help-msg { display: flex; gap: 8px; align-items: flex-start; }
  .help-msg--user { flex-direction: row-reverse; }
  .help-msg__col { display: flex; flex-direction: column; gap: 4px; max-width: calc(100% - 36px); }
  .help-msg--user .help-msg__col { align-items: flex-end; }
  .help-msg__meta { font-size: 11px; color: var(--text-3, #64748b); }
  .help-msg__meta strong { color: var(--text-2, #334155); font-weight: 600; }
  .help-msg__body { padding: 8px 12px; border-radius: 12px; font-size: 13px; line-height: 1.55; white-space: pre-wrap; word-wrap: break-word; }
  .help-msg--user .help-msg__body { background: var(--brand-50, #eef2ff); color: var(--text-1); border-bottom-right-radius: 3px; }
  .help-msg--op   .help-msg__body { background: var(--brand-600, #4f46e5); color: #ffffff; border-bottom-left-radius: 3px; }
  .help-msg__body--typing {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--brand-50, #eef2ff) !important;
    color: var(--text-3, #64748b) !important;
    padding: 10px 12px;
  }
  .help-msg__body--typing span { width: 6px; height: 6px; border-radius: 50%; background: var(--text-3, #94a3b8); display: inline-block; animation: help-typing 1.2s ease-in-out infinite; }
  .help-msg__body--typing span:nth-child(2) { animation-delay: 0.15s; }
  .help-msg__body--typing span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes help-typing {
    0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
    30%           { opacity: 1; transform: translateY(-2px); }
  }
  .help-msg__row-actions { margin-top: 4px; }
  .help-msg__promote { background: transparent; border: 1px solid var(--border-1, #cbd5e1); color: var(--text-3, #475569); border-radius: 9999px; padding: 2px 10px; font-size: 11px; cursor: pointer; }
  .help-msg__promote:hover { color: var(--brand-600, #4f46e5); border-color: var(--brand-600, #4f46e5); }
  .help-promote { display: flex; flex-direction: column; gap: 6px; padding: 10px; border: 1px solid var(--border-1, #e5e7eb); border-radius: 8px; background: var(--surface-0, #fff); width: 100%; max-width: 480px; }
  .help-promote__hint { font-size: 11.5px; color: var(--text-3, #64748b); line-height: 1.45; padding-bottom: 2px; }
  .help-promote__input { padding: 6px 8px; border: 1px solid var(--border-1, #cbd5e1); border-radius: 6px; font: inherit; font-size: 12px; }
  .help-promote__body { padding: 8px 10px; border: 1px solid var(--border-1, #cbd5e1); border-radius: 6px; font: inherit; font-size: 13px; line-height: 1.45; resize: vertical; min-height: 96px; }
  .help-promote__actions { display: flex; justify-content: flex-end; gap: 4px; }

  .help-thread__canned { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
  .help-canned-btn { padding: 4px 10px; background: var(--surface-0, #fff); border: 1px solid var(--border-1, #e5e7eb); border-radius: 9999px; color: var(--text-2, #334155); font: inherit; font-size: 11.5px; cursor: pointer; transition: border-color 0.12s ease, color 0.12s ease; }
  .help-canned-btn:hover { border-color: var(--brand-600, #4f46e5); color: var(--brand-600, #4f46e5); }
  .help-canned-btn:disabled { opacity: 0.5; cursor: default; }

  .help-thread__reply textarea { width: 100%; padding: 8px 10px; border: 1px solid var(--border-1, #e5e7eb); border-radius: 8px; font: inherit; font-size: 13px; resize: vertical; box-sizing: border-box; }
  .help-thread__reply-actions { display: flex; justify-content: flex-end; margin-top: 6px; }

  .help-status { font-size: 10.5px; font-weight: 700; padding: 2px 7px; border-radius: 9999px; text-transform: uppercase; letter-spacing: 0.04em; }
  .help-status--open          { background: #fef3c7; color: #92400e; }
  .help-status--awaiting_user { background: #dbeafe; color: #1e40af; }
  .help-status--resolved      { background: #dcfce7; color: #166534; }

  .help-avatar { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: var(--brand-600, #4f46e5); color: #ffffff; font-size: 11px; font-weight: 700; flex-shrink: 0; }

  /* Context map — host-supplied client_context grouped by source */
  .help-context-map {
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 12px;
  }
  .help-context-map__title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; color: var(--text-3, #64748b); margin-bottom: 10px; }
  .help-context-group { margin-bottom: 10px; }
  .help-context-group:last-child { margin-bottom: 0; }
  .help-context-group__label { font-size: 11px; font-weight: 700; color: var(--brand-600, #4f46e5); text-transform: lowercase; margin-bottom: 4px; }
  .help-context-group__dl { display: grid; grid-template-columns: minmax(120px, max-content) 1fr; gap: 4px 12px; margin: 0; font-size: 12px; }
  .help-context-group__dl dt { color: var(--text-3, #64748b); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  .help-context-group__dl dd { margin: 0; color: var(--text-1, #0f172a); overflow-wrap: anywhere; }
  .help-context-code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px; }
  .help-context-tag { display: inline-block; background: var(--surface-1, #f1f5f9); color: var(--text-2, #334155); border-radius: 4px; padding: 0 6px; margin-right: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; }
  .help-context-empty { color: var(--text-3, #94a3b8); }

  .btn--text { background: transparent; border: none; padding: 4px 8px; font-size: 12px; color: var(--text-3); cursor: pointer; }
  .btn--text:hover { color: var(--text-1); }
  .btn--sm { padding: 4px 10px; font-size: 12px; }
`;
